import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/booking.css';

const afternoonSlots = ["04:00 PM", "04:15 PM", "04:30 PM", "04:45 PM"];
const eveningSlots = [
  "05:00 PM", "05:15 PM", "05:30 PM", "05:45 PM",
  "08:00 PM", "08:15 PM", "08:30 PM", "08:45 PM",
  "09:00 PM", "09:15 PM", "09:30 PM", "09:45 PM"
];

function BookingPage() {
  const { doctorId } = useParams(); // This ID is guaranteed from the URL
  const navigate = useNavigate();

  // --- 1. STATE FOR DOCTOR & BOOKING ---
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [showModal, setShowModal] = useState(false);

  // --- 2. USER INPUTS ---
  const [userPhone, setUserPhone] = useState('');
  const [userEmail, setUserEmail] = useState('');

  // Validation & OTP
  const [errors, setErrors] = useState({});
  const [otpSent, setOtpSent] = useState(false);
  const [generatedOtp, setGeneratedOtp] = useState(null);
  const [enteredOtp, setEnteredOtp] = useState('');

  const getTodayString = () => new Date().toISOString().split('T')[0];
  const minDate = getTodayString();

  // --- 3. FETCH DOCTOR FROM DB ---
  useEffect(() => {
    // Determine the correct URL. 
    // If your backend route is just '/doctors/:id', remove '/find'. 
    // Assuming '/find/' works for you based on previous errors being resolved.
    const url = `${process.env.REACT_APP_API_BASE_URL}/doctors/find/${doctorId}`;
    
    axios.get(url)
      .then(res => {
        // FIX: Handle both wrappers (res.data.data) and direct objects (res.data)
        const docData = res.data.data || res.data;
        console.log("Fetched Doctor Data:", docData); // Debugging
        setDoctor(docData);
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching doctor:", err);
        setLoading(false);
      });

    // Auto-fill user details if logged in
    const userString = localStorage.getItem('DDoctorUser');
    if (userString) {
      const user = JSON.parse(userString);
      setUserEmail(user.email);
      if (user.phone) setUserPhone(user.phone);
    }
  }, [doctorId]);

  const validateInputs = () => {
    let tempErrors = {};
    let isValid = true;
    const phoneRegex = /^[0-9]{11,}$/;
    if (!userPhone) { tempErrors.phone = "Phone required."; isValid = false; }
    else if (!phoneRegex.test(userPhone)) { tempErrors.phone = "Invalid Phone."; isValid = false; }

    if (!userEmail) { tempErrors.email = "Email required."; isValid = false; }

    setErrors(tempErrors);
    return isValid;
  };

  const handleSlotClick = (slot) => {
    if (!selectedDate) return alert('Select a date first.');
    setSelectedSlot(slot);
    setErrors({});
    setOtpSent(false);
    setEnteredOtp('');
    setShowModal(true);
  };

  const handleSendOTP = (e) => {
    e.preventDefault();
    if (!validateInputs()) return;
    const code = Math.floor(1000 + Math.random() * 9000);
    setGeneratedOtp(code);
    setOtpSent(true);
    alert(`[SMS SIMULATION] Your OTP code is: ${code}`);
  };

  const handleConfirmBooking = (e) => {
    e.preventDefault();
  if (parseInt(enteredOtp) !== generatedOtp) {
      alert("Invalid OTP.");
      return;
    }

    const userString = localStorage.getItem('DDoctorUser');
    if (!userString) {
      alert('Please log in to book.');
      return;
    }
    const user = JSON.parse(userString);

    // SAFETY CHECK: Ensure doctor is loaded
    if (!doctor) {
        alert("Doctor details are missing. Please refresh.");
        return;
    }

    const bookingDetails = {
      userId: user._id,
      patientName: user.name,
      contactPhone: userPhone,
      contactEmail: userEmail,

      // FIX: Use the ID from the URL (guaranteed) if doctor._id is missing
      doctorId: doctor._id || doctorId, 
      doctorName: doctor.name,
      doctorImage: doctor.image,
      specialty: doctor.specialty,
      fee: doctor.fee,
      
      appointmentDate: selectedDate,
      appointmentTime: selectedSlot
    };

    console.log("Sending Booking:", bookingDetails); // Debugging

    axios.post(process.env.REACT_APP_API_BASE_URL + "/bookings/add", bookingDetails)
      .then(res => {
        setShowModal(false);
        alert('Booking Confirmed! Proceeding to payment.');
        navigate('/payment');
      })
      .catch(err => {
        console.error("Booking Error:", err);
        // Show the actual error from the server
        alert(`Error creating booking: ${err.response?.data?.message || err.message}`);
      });
  };

  if (loading) return <div className="text-center mt-5">Loading Doctor Details...</div>;
  if (!doctor) return <div className="text-center mt-5">Doctor not found.</div>;

  return (
    <>
      <section className="hero-section text-center">
        <div className="container">
          <h1 className="display-4 fw-bold" style={{ fontSize: '2.5rem' }}>
            Book Appointment
          </h1>
          <p className="lead" style={{ fontSize: '1.1rem' }}>Specialist Doctors</p>
        </div>
      </section>
      <div className="container py-4">
        <div className="card mb-4 shadow-sm p-3">
          <div className="d-flex align-items-center">
            {/* Handle Image Path: Check if base64 or relative */}
            <img
              src={doctor.image && doctor.image.startsWith('data')
                ? doctor.image
                : `/images/${doctor.image || 'default.png'}`}
              className="rounded-circle me-3"
              alt={doctor.name}
              style={{ width: '100px', height: '100px', objectFit: 'cover' }}
            />
            <div>
              <h4 className="mb-1 fw-bold">{doctor.name}</h4>
              <p className="mb-1 text-muted">{doctor.specialty} Specialist</p>
              <p className="mb-0 fw-bold text-primary">Fee: Rs. {doctor.fee}</p>
            </div>
          </div>
        </div>

        <label className="form-label fw-bold">Select Date</label>
        <input type="date" className="form-control mb-4" value={selectedDate} min={minDate} onChange={(e) => setSelectedDate(e.target.value)} />

        <label className="form-label fw-bold">Select Time Slot</label>
        <div>
          <div className="d-flex flex-wrap gap-2">
            {afternoonSlots.map(slot => (
              <button key={slot} className={`btn ${selectedSlot === slot ? 'btn-warning' : 'btn-outline-secondary'}`} onClick={() => handleSlotClick(slot)}>{slot}</button>
            ))}
            {eveningSlots.map(slot => (
              <button key={slot} className={`btn ${selectedSlot === slot ? 'btn-warning' : 'btn-outline-secondary'}`} onClick={() => handleSlotClick(slot)}>{slot}</button>
            ))}
          </div>
        </div>
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="booking-modal-overlay">
          <div className="booking-modal-content">
            <div className="d-flex justify-content-between mb-3">
              <h5>Confirm Booking</h5>
              <button className="btn-close" onClick={() => setShowModal(false)}></button>
            </div>
            <form onSubmit={otpSent ? handleConfirmBooking : handleSendOTP}>
              <div className="mb-3">
                <label>Phone Number</label>
                <input type="number" className="form-control" value={userPhone} onChange={(e) => setUserPhone(e.target.value)} disabled={otpSent} />
              </div>
              <div className="mb-3">
                <label>Email Address</label>
                <input type="email" className="form-control" value={userEmail} onChange={(e) => setUserEmail(e.target.value)} disabled={otpSent} />
              </div>

              {otpSent && (
                <div className="mb-3">
                  <label className="text-success fw-bold">Enter OTP</label>
                  <input type="number" className="form-control" value={enteredOtp} onChange={(e) => setEnteredOtp(e.target.value)} />
                </div>
              )}

              <button type="submit" className={`btn w-100 ${otpSent ? 'btn-success' : 'btn-primary'}`}>
                {otpSent ? 'Verify & Book' : 'Send OTP'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default BookingPage;